import { IAdvertiserFormData } from '@/Interfaces/Advertiser.interface';
import { IAgencyFormData } from '@/Interfaces/Agency.interface';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { DEFAULT_ADVERTISER } from '@/constants/advertisers';
import { DEFAULT_AGENCY } from '@/constants/agencies';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAgenciesData, selectSelectedAgency } from '@/store/selectors/agencies.selectors';
import { getAdvertiserCategories } from '@/store/slices/advertisers.slice';
import { clearSelectedAgency, getAgencies, resetAgenciesPageState } from '@/store/slices/agencies.slice';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { Card } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import AddAdvertiserModal from './AddAdvertiserModal';
import AgenciesGrid from './AgenciesGrid';
import AgencyModal from './AgencyModal';

const Agencies = () => {
  const dispatch = useAppDispatch();

  const { data, loading: dataLoading, filters, total } = useAppSelector(selectAgenciesData);

  const [loading, setLoading] = useState<boolean>(false);
  const [openAgencyModal, setOpenAgencyModal] = useState<boolean>(false);
  const [openAdvertiserModal, setOpenAdvertiserModal] = useState<boolean>(false);

  const isLoading = useMemo(() => dataLoading || loading, [dataLoading, loading]);

  const selectedAgency = useAppSelector(selectSelectedAgency);

  //#region Agency Form
  const agencyForm = useForm<IAgencyFormData>({
    defaultValues: selectedAgency || DEFAULT_AGENCY,
  });

  const { control: agencyFormControl, setValue: setAgencyFormValue } = agencyForm;

  const selectedClient: IClientControlOption | null = useWatch({ control: agencyFormControl, name: 'client' });
  const selectedAdvertisers: IControlOption[] = useWatch({ control: agencyFormControl, name: 'advertisers' });
  //#endregion Agency Form

  //#region Advertiser Form
  const advertisersForm = useForm<IAdvertiserFormData>({
    defaultValues: { ...DEFAULT_ADVERTISER, client: selectedClient },
  });

  const { reset: resetAdvertisersForm } = advertisersForm;
  //#endregion Advertiser Form

  useEffect(() => {
    resetAdvertisersForm({ ...DEFAULT_ADVERTISER, client: selectedClient });
  }, [selectedClient, resetAdvertisersForm]);

  useEffect(() => {
    if (selectedAgency) {
      setOpenAgencyModal(true);
    }
  }, [selectedAgency, setOpenAgencyModal]);

  useEffect(() => {
    dispatch(getAgencies());
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(getClients());
    dispatch(getWhitelabels());
    dispatch(getAdvertiserCategories());

    return () => {
      dispatch(resetAgenciesPageState());
    };
  }, [dispatch]);

  const handleCloseAgencyModal = useCallback(
    (refresh?: boolean) => {
      if (refresh) {
        dispatch(getAgencies());
      }
      dispatch(clearSelectedAgency());
      setOpenAgencyModal(false);
    },
    [dispatch, setOpenAgencyModal],
  );

  const handleCloseAdvertiserModal = useCallback(
    (newAdvertiser: IControlOption | null, refresh?: boolean) => {
      if (refresh) {
        dispatch(getClients({ refresh }));
      }
      if (newAdvertiser) {
        setAgencyFormValue('advertisers', [...selectedAdvertisers, newAdvertiser]);
      }
      setOpenAdvertiserModal(false);
    },
    [dispatch, setOpenAdvertiserModal, selectedAdvertisers, setAgencyFormValue],
  );

  return (
    <div className='w-full p-[30px]'>
      <Card className='h-full'>
        <AgenciesGrid
          data={data}
          loading={isLoading}
          rowCount={total}
          handleCreationModalOpen={() => setOpenAgencyModal(true)}
        />
        <AgencyModal
          form={agencyForm}
          open={openAgencyModal}
          onClose={handleCloseAgencyModal}
          selectedAgency={selectedAgency}
          loading={isLoading}
          setLoading={setLoading}
          openAdvertiserModal={() => setOpenAdvertiserModal(true)}
        />
        <AddAdvertiserModal
          form={advertisersForm}
          open={openAdvertiserModal}
          onClose={handleCloseAdvertiserModal}
          loading={isLoading}
          setLoading={setLoading}
        />
      </Card>
    </div>
  );
};

export default Agencies;
